import { useState, type FormEvent, useRef, useEffect } from "react";
import { useWishlistStore, useAuthStore } from "../../store/useStore";
import { Avatar, Btn } from "../ui";

interface NavbarProps {
  page: string;
  setPage: (p: string) => void;
  onSearch: (q: string) => void;
}

const NAV = [
  { id: "home",         label: "Store" },
  { id: "browse",       label: "Browse" },
  { id: "new-releases", label: "New Releases" },
  { id: "deals",        label: "Deals" },
];

export function Navbar({ page, setPage, onSearch }: NavbarProps) {
  const [query, setQuery]           = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [userMenu, setUserMenu]     = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const { ids }   = useWishlistStore();
  const { user, logout } = useAuthStore();

  useEffect(() => { if (searchOpen) searchRef.current?.focus(); }, [searchOpen]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (query.trim()) { onSearch(query.trim()); setPage("browse"); }
    setSearchOpen(false);
  };

  return (
    <nav
      style={{
        position: "sticky", top: 0, zIndex: 50,
        background: "var(--bg-nav)", backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        borderBottom: "1px solid var(--border)",
        height: 60, display: "flex", alignItems: "center",
        padding: "0 28px", gap: 0,
      }}
    >
      {/* Logo */}
      <button
        onClick={() => setPage("home")}
        style={{ background: "none", border: "none", cursor: "pointer", padding: "0 20px 0 0", marginRight: 16, borderRight: "1px solid var(--border)", flexShrink: 0 }}
      >
        <span style={{ fontFamily: "var(--font-display)", fontSize: 24, fontWeight: 900, letterSpacing: 0.5 }}>
          <span style={{ color: "var(--accent-bright)" }}>GS</span>
          <span style={{ color: "var(--text-muted)", fontSize: 13, fontWeight: 700, marginLeft: 2 }}>STORE</span>
        </span>
      </button>

      {/* Desktop Nav links */}
      <div className="desktop-nav" style={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
        {NAV.map(({ id, label }) => (
          <button key={id} className={`nav-link${page === id ? " active" : ""}`} onClick={() => setPage(id)}>
            {label}
          </button>
        ))}
      </div>

      {/* Mobile menu button */}
      <button 
        className="mobile-menu-btn"
        onClick={() => setMobileMenu(!mobileMenu)}
        style={{
          display: "none", background: "none", border: "none", 
          cursor: "pointer", padding: "8px", borderRadius: "var(--radius-sm)",
          color: "var(--text-primary)", fontSize: 20
        }}
      >
        {mobileMenu ? "✕" : "☰"}
      </button>

      {/* Right controls */}
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {searchOpen ? (
          <form onSubmit={submit} style={{ display: "flex" }}>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", fontSize: 14, pointerEvents: "none" }}>🔍</span>
              <input
                ref={searchRef}
                className="gs-input"
                style={{ width: 220, paddingLeft: 34, height: 36 }}
                placeholder="Search games…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onBlur={() => { if (!query) setSearchOpen(false); }}
              />
            </div>
          </form>
        ) : (
          <button className="nav-link" onClick={() => setSearchOpen(true)} style={{ fontSize: 17, padding: "5px 10px" }}>🔍</button>
        )}

        <button className="nav-link" onClick={() => setPage("wishlist")} style={{ position: "relative", gap: 5 }}>
          <span style={{ color: "var(--color-wishlist)", fontSize: 16 }}>♥</span>
          <span>Wishlist</span>
          {ids.length > 0 && (
            <span style={{ background: "var(--accent)", color: "#fff", borderRadius: "50%", width: 17, height: 17, fontSize: 10, display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 800 }}>
              {ids.length}
            </span>
          )}
        </button>

        <div style={{ width: 1, height: 28, background: "var(--border)", margin: "0 4px" }} />

        {user ? (
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setUserMenu((v) => !v)}
              style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, padding: "4px 8px", borderRadius: "var(--radius-sm)" }}
            >
              <Avatar name={user.name} size={30} />
              <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>{user.name}</span>
              <span style={{ color: "var(--text-muted)", fontSize: 11 }}>▾</span>
            </button>
            {userMenu && (
              <div style={{ position: "absolute", top: "calc(100% + 10px)", right: 0, background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "var(--radius-md)", padding: 8, minWidth: 185, boxShadow: "0 12px 40px rgba(0,0,0,0.5)", zIndex: 200 }}>
                <div style={{ padding: "8px 12px 12px", borderBottom: "1px solid var(--border)", marginBottom: 6 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{user.name}</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{user.role}</div>
                </div>
                {[{ label: "My Account", p: "account" }, { label: "Purchase History", p: "account" }, { label: "Publisher Portal", p: "publisher" }].map(({ label, p }) => (
                  <button key={label} className="nav-link" style={{ width: "100%", textAlign: "left", display: "block", padding: "7px 12px" }} onClick={() => { setPage(p); setUserMenu(false); }}>{label}</button>
                ))}
                <div style={{ height: 1, background: "var(--border)", margin: "6px 0" }} />
                <button className="nav-link" style={{ width: "100%", textAlign: "left", color: "var(--color-wishlist)", padding: "7px 12px" }} onClick={() => { logout(); setUserMenu(false); }}>Sign Out</button>
              </div>
            )}
          </div>
        ) : (
          <>
            <Btn variant="ghost" size="sm" onClick={() => setPage("signin")}>Sign In</Btn>
            <Btn size="sm" onClick={() => setPage("signup")}>Create Account</Btn>
          </>
        )}
      </div>

      {/* Mobile menu overlay */}
      {mobileMenu && (
        <div 
          className="mobile-menu-overlay"
          style={{
            position: "fixed", top: 60, left: 0, right: 0, bottom: 0,
            background: "var(--bg-surface)", zIndex: 40,
            padding: "20px 16px", overflow: "auto"
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {NAV.map(({ id, label }) => (
              <button 
                key={id} 
                className={`nav-link${page === id ? " active" : ""}`} 
                onClick={() => { setPage(id); setMobileMenu(false); }}
                style={{ 
                  width: "100%", textAlign: "left", padding: "12px 16px",
                  fontSize: 16, borderBottom: "1px solid var(--border)"
                }}
              >
                {label}
              </button>
            ))}
            
            <div style={{ height: 1, background: "var(--border)", margin: "12px 0" }} />
            
            <button 
              className="nav-link" 
              onClick={() => { setPage("wishlist"); setMobileMenu(false); }}
              style={{ 
                width: "100%", textAlign: "left", padding: "12px 16px",
                fontSize: 16, display: "flex", alignItems: "center", gap: 8
              }}
            >
              <span style={{ color: "var(--color-wishlist)" }}>♥</span>
              Wishlist
              {ids.length > 0 && (
                <span style={{ 
                  background: "var(--accent)", color: "#fff", borderRadius: "50%", 
                  width: 20, height: 20, fontSize: 11, display: "inline-flex", 
                  alignItems: "center", justifyContent: "center", fontWeight: 800, marginLeft: "auto"
                }}>
                  {ids.length}
                </span>
              )}
            </button>
            
            {user ? (
              <>
                <div style={{ height: 1, background: "var(--border)", margin: "12px 0" }} />
                <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)" }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{user.name}</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{user.role}</div>
                </div>
                {[
                  { label: "My Account", p: "account" }, 
                  { label: "Purchase History", p: "account" }, 
                  { label: "Publisher Portal", p: "publisher" }
                ].map(({ label, p }) => (
                  <button 
                    key={label} 
                    className="nav-link" 
                    style={{ 
                      width: "100%", textAlign: "left", display: "block", 
                      padding: "12px 16px", fontSize: 14 
                    }} 
                    onClick={() => { setPage(p); setMobileMenu(false); }}
                  >
                    {label}
                  </button>
                ))}
                <button 
                  className="nav-link" 
                  style={{ 
                    width: "100%", textAlign: "left", color: "var(--color-wishlist)", 
                    padding: "12px 16px", fontSize: 14 
                  }} 
                  onClick={() => { logout(); setMobileMenu(false); }}
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <div style={{ height: 1, background: "var(--border)", margin: "12px 0" }} />
                <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "12px 0" }}>
                  <Btn variant="ghost" size="sm" onClick={() => { setPage("signin"); setMobileMenu(false); }} style={{ width: "100%", justifyContent: "center" }}>
                    Sign In
                  </Btn>
                  <Btn size="sm" onClick={() => { setPage("signup"); setMobileMenu(false); }} style={{ width: "100%", justifyContent: "center" }}>
                    Create Account
                  </Btn>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
